<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Checkbox from '../../common/form/checkbox/Checkbox.vue';
import type ErrorModel from '../../../js/model/Error.js';
import FormRow from '../../common/form/FormRow.vue';
import GenericSelect from '../../common/form/select/GenericSelect.vue';
import type MemberSettings from '../../../js/dto/MemberSettings.ts';
import MembershipNumberPreview from '../../members/membership-number/MembershipNumberPreview.vue';
import MembershipNumberRepository from '../../../js/repository/MembershipNumberRepository.js';
import { useI18n } from '@/composables/i18n';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    components: {
        'kgr-membership-number-preview': MembershipNumberPreview,
        'kgr-generic-select': GenericSelect,
        'kgr-checkbox': Checkbox,
        'kgr-form-row': FormRow,
    },
});

const props = defineProps<{
    clubId: number
    memberSettings: MemberSettings
    error: ErrorModel
}>();

const emit = defineEmits<{
    enter: []
}>();

const { i18n } = useI18n();
const loading = ref(false);
const nextMembershipNumber = ref<string | null>(null);
const membershipNumberDigits = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const REGEX_PREFIX = /\d/;
const REGEX_SUFFIX = /^\d+/;

const membershipNumberRangeStartMax = computed(() => {
    const digits = props.memberSettings.membershipNumberDigits;
    if (membershipNumberDigits.includes(digits)) {
        return Number('9'.repeat(digits));
    }
    return 99999999;
});

const isDisabled = computed(() => props.memberSettings.managedByFederation);

onMounted(() => {
    if (!isDisabled.value) {
        loadMembershipNumber();
    }
});

function onKeyUpEnter() {
    emit('enter');
}

function loadMembershipNumber() {
    loading.value = true;
    MembershipNumberRepository.getNext(props.clubId, props.memberSettings)
        .then((number: string) => {
            nextMembershipNumber.value = number;
            loading.value = false;
        })
        .catch(() => {
            loading.value = false;
        });
}

function onUpdatedMemberSettings() {
    if (validate()) {
        loadMembershipNumber();
    }
}

function validate(): boolean {
    const prefixValid = !REGEX_PREFIX.test(props.memberSettings.membershipNumberPrefix);
    const suffixValid = !REGEX_SUFFIX.test(props.memberSettings.membershipNumberSuffix);
    props.error.clear('memberSettings.membershipNumberPrefix');
    props.error.clear('memberSettings.membershipNumberSuffix');
    if (!prefixValid) {
        props.error.add('memberSettings.membershipNumberPrefix', i18n.t('Der Prefix darf keine Zahl enthalten.'));
    }
    if (!suffixValid) {
        props.error.add('memberSettings.membershipNumberSuffix', i18n.t('Der Suffix darf nicht mit einer Zahl beginnen.'));
    }
    return prefixValid && suffixValid;
}
</script>

<template>
    <div>
        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ i18n.t('Mitgliedsnummern') }}
            <img
                src="@img/info.png"
                alt=""
                :title="i18n.t('Clubity generiert nach dem unten definierten Schema automatisch Mitgliedsnummern für aktivierte Mitglieder.')"
            />
        </h3>

        <div
            v-if="isDisabled"
            class="kgr-alert kgr-alert--info"
        >
            {{ i18n.t('Die Verwaltung der Mitgliedsnummern wird vom Träger übernommen.') }}
        </div>

        <div v-else>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <div class="animated fadeIn kgr-grid__cell">
                            <kgr-form-row
                                :label="i18n.t('Prefix')"
                                :error="error"
                                field-name="memberSettings.membershipNumberPrefix"
                            >
                                <input
                                    v-model.trim="memberSettings.membershipNumberPrefix"
                                    type="text"
                                    class="kgr-input"
                                    maxlength="6"
                                    @keyup.enter="onKeyUpEnter"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :label="i18n.t('Suffix')"
                                :error="error"
                                field-name="memberSettings.membershipNumberSuffix"
                            >
                                <input
                                    v-model.trim="memberSettings.membershipNumberSuffix"
                                    type="text"
                                    class="kgr-input"
                                    maxlength="6"
                                    @keyup.enter="onKeyUpEnter"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :label="i18n.t('Nummernkreis von')"
                                :error="error"
                                field-name="memberSettings.membershipNumberRangeStart"
                                required
                            >
                                <input
                                    v-model.number="memberSettings.membershipNumberRangeStart"
                                    type="number"
                                    min="1"
                                    :max="membershipNumberRangeStartMax"
                                    class="kgr-input"
                                    @keyup.enter="onKeyUpEnter"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="memberSettings.membershipNumberLeadingZeros"
                            >
                                <kgr-checkbox
                                    v-model="memberSettings.membershipNumberLeadingZeros"
                                    :label="i18n.t('Führende Nullen verwenden')"
                                    class="kgr-row"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                v-if="memberSettings.membershipNumberLeadingZeros"
                                :label="i18n.t('Stellen des Nummernkreises')"
                                :error="error"
                                field-name="memberSettings.membershipNumberDigits"
                                class="animated fadeIn"
                            >
                                <kgr-generic-select
                                    v-model="memberSettings.membershipNumberDigits"
                                    :options="membershipNumberDigits"
                                    no-null-option
                                    @post-input="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="memberSettings.recycleNumbers"
                            >
                                <kgr-checkbox
                                    v-model="memberSettings.membershipNumberRecycling"
                                    :label="i18n.t('Letzte Nummer des Mitglieds bei zusätzlichem Vertrag wiederverwenden')"
                                    class="kgr-row"
                                />
                            </kgr-form-row>

                            <kgr-form-row
                                :error="error"
                                field-name="memberSettings.recycleNumbers"
                            >
                                <kgr-checkbox
                                    v-model="memberSettings.counterBasedOnPreAndSuffix"
                                    :label="i18n.t('Nummernkreis für jeden Pre- oder Suffix verwenden (nur relevant bei Änderung des Pre- oder Suffixes)')"
                                    class="kgr-row"
                                    @change="onUpdatedMemberSettings()"
                                />
                            </kgr-form-row>
                        </div>
                    </div>

                    <kgr-membership-number-preview
                        v-if="nextMembershipNumber"
                        :number="nextMembershipNumber"
                        :loading="loading"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
